/**
 * ProductImagesManager
 * ────────────────────
 * Multi-image manager for product forms.
 * Props:
 *   images   — array of { url, altText, isPrimary, sortOrder }
 *   onChange — (newImages) => void
 *   maxImages — default 10
 */
import { useState, useRef } from 'react'
import { Plus, Star, X, ImageOff, GripVertical } from 'lucide-react'
import MediaPickerModal from './MediaPickerModal'
import { imgUrl } from '@/utils/img'

// ── Tile dimensions ───────────────────────────────────────────────────────────
const TILE = 96

// ── Single image tile ─────────────────────────────────────────────────────────
function ImageTile({ img, index, onRemove, onSetPrimary, total }) {
  const [hovered, setHovered] = useState(false)
  const isPrimary = img.isPrimary

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        width: TILE,
        height: TILE,
        borderRadius: 12,
        overflow: 'hidden',
        flexShrink: 0,
        cursor: 'default',
        border: isPrimary ? '2.5px solid #FF6D00' : '2.5px solid transparent',
        transition: 'border-color 0.18s, box-shadow 0.18s',
        boxShadow: isPrimary
          ? '0 0 0 3px rgba(255,109,0,0.15)'
          : hovered
            ? '0 4px 16px rgba(0,0,0,0.13)'
            : '0 1px 4px rgba(0,0,0,0.07)',
        background: '#F0F0F5',
      }}
    >
      {/* Image */}
      {img.url ? (
        <img
          src={imgUrl(img.url)}
          alt={img.altText || ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={(e) => { e.target.style.display = 'none' }}
        />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ImageOff size={20} style={{ color: '#C4C4C4' }} />
        </div>
      )}

      {/* Primary badge */}
      {isPrimary && !hovered && (
        <div style={{
          position: 'absolute', bottom: 5, right: 0, left: 0,
          display: 'flex', justifyContent: 'center',
        }}>
          <span style={{
            background: '#FF6D00',
            color: '#fff',
            fontSize: 9,
            fontWeight: 800,
            fontFamily: 'Cairo, sans-serif',
            padding: '2px 6px',
            borderRadius: 6,
            letterSpacing: '0.3px',
          }}>
            رئيسية
          </span>
        </div>
      )}

      {/* Hover overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'rgba(10,10,20,0.58)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 7,
        opacity: hovered ? 1 : 0,
        transition: 'opacity 0.15s ease',
        backdropFilter: 'blur(2px)',
      }}>
        {/* Set primary button — only if not already primary */}
        {!isPrimary && (
          <button
            onClick={onSetPrimary}
            title="جعلها رئيسية"
            style={{
              display: 'flex', alignItems: 'center', gap: 3,
              padding: '4px 8px', borderRadius: 7,
              background: '#FF6D00',
              border: 'none', cursor: 'pointer',
              color: '#fff',
              fontSize: 9, fontWeight: 800, fontFamily: 'Cairo, sans-serif',
              transition: 'transform 0.1s',
              whiteSpace: 'nowrap',
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.93)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <Star size={10} fill="#fff" />
            رئيسية
          </button>
        )}

        {/* Delete button */}
        <button
          onClick={onRemove}
          title="حذف الصورة"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 26, height: 26, borderRadius: 8,
            background: 'rgba(186,26,26,0.85)',
            border: 'none', cursor: 'pointer',
            color: '#fff',
            transition: 'transform 0.1s, background 0.12s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#BA1A1A'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(186,26,26,0.85)'}
          onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.9)'}
          onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <X size={12} />
        </button>
      </div>

      {/* Index indicator (top-left corner, tiny) */}
      <div style={{
        position: 'absolute', top: 5, right: 5,
        width: 16, height: 16, borderRadius: 5,
        background: isPrimary ? '#FF6D00' : 'rgba(0,0,0,0.38)',
        color: '#fff',
        fontSize: 9, fontWeight: 800,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Cairo, sans-serif',
        opacity: hovered ? 0 : 1,
        transition: 'opacity 0.15s',
      }}>
        {index + 1}
      </div>
    </div>
  )
}

// ── Add tile ──────────────────────────────────────────────────────────────────
function AddTile({ onClick }) {
  const [hovered, setHovered] = useState(false)

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: TILE, height: TILE,
        borderRadius: 12,
        border: `2px dashed ${hovered ? '#000666' : '#C8C8D0'}`,
        background: hovered ? 'rgba(0,6,102,0.04)' : '#FAFAFA',
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 5,
        transition: 'all 0.18s ease',
        flexShrink: 0,
      }}
    >
      <div style={{
        width: 28, height: 28, borderRadius: 8,
        background: hovered ? '#000666' : '#E8E8EE',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.18s',
      }}>
        <Plus size={14} style={{ color: hovered ? '#fff' : '#767683' }} />
      </div>
      <span style={{
        fontSize: 9, fontWeight: 700,
        fontFamily: 'Cairo, sans-serif',
        color: hovered ? '#000666' : '#A0A0B0',
        transition: 'color 0.18s',
      }}>
        أضف صورة
      </span>
    </button>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ProductImagesManager({
  images = [],
  onChange,
  maxImages = 10,
}) {
  const [pickerOpen, setPickerOpen] = useState(false)

  const handleAdd = ({ url }) => {
    if (images.length >= maxImages) return
    const isPrimary = images.length === 0  // first image becomes primary
    const newImg = { url, altText: '', isPrimary, sortOrder: images.length }
    onChange([...images, newImg])
    setPickerOpen(false)
  }

  const handleRemove = (idx) => {
    const next = images.filter((_, i) => i !== idx)
    // If removed image was primary and there are remaining images, make the first one primary
    if (images[idx]?.isPrimary && next.length > 0) {
      next[0] = { ...next[0], isPrimary: true }
    }
    onChange(next.map((img, i) => ({ ...img, sortOrder: i })))
  }

  const handleSetPrimary = (idx) => {
    onChange(images.map((img, i) => ({ ...img, isPrimary: i === idx })))
  }

  const canAdd = images.length < maxImages

  return (
    <div>
      {/* Header row */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 10,
      }}>
        <label style={{
          fontSize: 12, fontWeight: 700, color: '#454652',
          fontFamily: 'Tajawal, sans-serif',
        }}>
          صور المنتج
        </label>
        <span style={{
          fontSize: 11, fontWeight: 600, color: '#A0A0B0',
          fontFamily: 'Tajawal, sans-serif',
        }}>
          {images.length} / {maxImages}
        </span>
      </div>

      {/* Grid */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        direction: 'rtl',
      }}>
        {images.map((img, idx) => (
          <ImageTile
            key={`${img.url}-${idx}`}
            img={img}
            index={idx}
            total={images.length}
            onRemove={() => handleRemove(idx)}
            onSetPrimary={() => handleSetPrimary(idx)}
          />
        ))}
        {canAdd && <AddTile onClick={() => setPickerOpen(true)} />}
      </div>

      {/* Helper text */}
      {images.length === 0 && (
        <p style={{
          fontSize: 11, color: '#A0A0B0',
          fontFamily: 'Tajawal, sans-serif',
          marginTop: 8,
        }}>
          أضف حتى {maxImages} صور — أول صورة تضيفها تصبح الصورة الرئيسية تلقائياً
        </p>
      )}
      {images.length > 0 && (
        <p style={{
          fontSize: 11, color: '#A0A0B0',
          fontFamily: 'Tajawal, sans-serif',
          marginTop: 8,
        }}>
          اضغط على أي صورة لتعيينها كصورة رئيسية أو لحذفها
        </p>
      )}

      <MediaPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleAdd}
        title="إضافة صورة للمنتج"
        folder="products"
      />
    </div>
  )
}
